import { motion } from 'framer-motion'
import { useInView } from 'framer-motion'
import { Link } from 'react-router-dom'
import { useRef } from 'react'
import { useLanguage } from '../contexts/LanguageContext'

const Pricing = () => {
  const { t, getLangPrefix } = useLanguage()
  const langPrefix = getLangPrefix()
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, amount: 0.3 })

  return (
    <section
      id="pricing"
      ref={ref}
      className="py-4 md:py-8 lg:py-12 xl:py-16 bg-gradient-to-b from-white via-gray-50 to-white relative overflow-hidden"
    >
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.1, 0.2, 0.1],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
        className="absolute bottom-20 right-0 w-96 h-96 bg-gradient-to-br from-primary/20 to-primary-dark/10 rounded-full blur-3xl"
      />

      <div className="container mx-auto px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-4 md:mb-8 lg:mb-12"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-3 md:mb-4 lg:mb-6 text-gray-900">
            {t.pricing.title}
          </h2>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-600 max-w-2xl mx-auto px-4">
            {t.pricing.description}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 50 }}
          animate={isInView ? { opacity: 1, scale: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="max-w-2xl mx-auto"
        >
          <motion.div
            whileHover={{ scale: 1.02, y: -5 }}
            className="relative bg-white rounded-2xl md:rounded-3xl p-4 md:p-6 lg:p-8 xl:p-12 border-2 border-primary shadow-2xl premium-glow overflow-hidden"
          >
            <motion.div
              className="absolute top-0 left-0 right-0 h-2 gradient-bg"
              initial={{ scaleX: 0 }}
              animate={isInView ? { scaleX: 1 } : {}}
              transition={{ duration: 0.8, delay: 0.5 }}
            />

            <motion.div
              initial={{ opacity: 0, rotate: -45 }}
              animate={isInView ? { opacity: 1, rotate: -45 } : {}}
              transition={{ delay: 0.8 }}
              className="absolute top-4 md:top-6 -right-8 md:-right-12 bg-gradient-to-r from-primary to-primary-dark text-white px-12 md:px-16 py-1.5 md:py-2 text-[10px] md:text-xs font-bold tracking-wider shadow-lg"
            >
              {t.pricing.popular}
            </motion.div>

            <div className="text-center mb-4 md:mb-6 lg:mb-8">
              <h3 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-black mb-2 md:mb-3 lg:mb-4 text-gray-900">
                Juvenis
              </h3>
              <div className="flex items-baseline justify-center gap-1 md:gap-1.5 mb-1.5 md:mb-2">
                <motion.span
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={isInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ delay: 0.4, type: 'spring', stiffness: 200 }}
                  className="text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-black gradient-text"
                >
                      $99
                </motion.span>
                <span className="text-lg md:text-xl lg:text-2xl text-gray-600">/ месяц</span>
              </div>
              <p className="text-sm md:text-base lg:text-lg text-gray-600 px-2">
                {t.pricing.note}
              </p>
            </div>

            <div className="space-y-1.5 md:space-y-2 lg:space-y-3 mb-4 md:mb-6 lg:mb-8">
              {t.pricing.features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.6 + index * 0.1 }}
                  className="flex items-center gap-1.5 md:gap-2 lg:gap-3"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={isInView ? { scale: 1 } : {}}
                    transition={{
                      delay: 0.6 + index * 0.1,
                      type: 'spring',
                      stiffness: 200,
                    }}
                    className="w-4 h-4 md:w-5 md:h-5 lg:w-6 lg:h-6 gradient-bg rounded-full flex items-center justify-center flex-shrink-0"
                  >
                    <svg
                      className="w-2.5 h-2.5 md:w-3 md:h-3 lg:w-4 lg:h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </motion.div>
                  <span className="text-sm md:text-base lg:text-lg text-gray-700 font-medium">{feature}</span>
                </motion.div>
              ))}
            </div>

            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="mb-3 md:mb-4 lg:mb-6"
            >
              <Link
                to={`${langPrefix}/waitlist`}
                className="block w-full py-3 md:py-4 lg:py-5 gradient-bg text-white rounded-xl md:rounded-2xl font-bold text-base md:text-lg lg:text-xl text-center shadow-xl hover:shadow-2xl transition-all duration-300 premium-glow"
              >
                {t.pricing.startFree}
              </Link>
            </motion.div>

            <p className="text-center text-sm md:text-base text-gray-500">
              {t.pricing.note}
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}

export default Pricing
